import { TimeoutError } from './withTimeout';

/**
 * Profile load with ONE retry (v3.84.8, BACKLOG P1 "PROFILE READ ERROR SHOWS
 * ONBOARDING").
 *
 * App.js used to run `getDoc(users/{uid})` inside a try/catch that swallowed
 * every non-timeout error, leaving `profile` unset — and `!user.profile`
 * rendered the OnboardingModal to an EXISTING teacher (7-day CI evidence:
 * every first-attempt "Welcome to Your Dashboard" on tls grade-report-prek).
 * The distinction lives here now:
 *   - the doc does not exist        → { profile: null }   (onboarding, as before)
 *   - the doc exists                → { profile: data }
 *   - the read timed out            → TimeoutError rethrown at once (boot stall)
 *   - the read failed otherwise     → wait, retry ONCE; a second failure throws
 *                                     ProfileLoadError (boot stall with Retry)
 * A failed read is never "no profile".
 */
export const PROFILE_RETRY_DELAY_MS = 1200;

export class ProfileLoadError extends Error {
  constructor(cause) {
    super(`Couldn't load your profile — retry${cause?.code ? ` (${cause.code})` : ''}`);
    this.name = 'ProfileLoadError';
    this.cause = cause;
    this.code = cause?.code || null;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {() => Promise<{exists: () => boolean, data: () => object}>} readSnap
 *   one attempt at the users/{uid} read (already wrapped in withTimeout by the caller)
 * @returns {Promise<{profile: object|null, attempts: number}>}
 */
export async function loadProfileOnce(readSnap, { retryDelayMs = PROFILE_RETRY_DELAY_MS, wait = sleep } = {}) {
  let attempts = 0;
  for (;;) {
    attempts += 1;
    try {
      const snap = await readSnap();
      return { profile: snap.exists() ? snap.data() : null, attempts };
    } catch (err) {
      if (err instanceof TimeoutError || err?.name === 'TimeoutError') throw err; // a hang is "state unknown", not "retry"
      if (attempts >= 2) throw new ProfileLoadError(err);
      await wait(retryDelayMs);
    }
  }
}
